import { useLocation, useNavigate } from "react-router-dom";
import { adminEmails } from "../../../constants.ts";
import { db } from "../db.ts";
import { AppHeader, type Tab } from "../components/AppHeader.tsx";
import { Auth } from "../components/Auth.tsx";
import { PhoneVerification } from "../components/PhoneVerification.tsx";
import { EventList } from "../components/EventList.tsx";
import { EventSwiper } from "../components/EventSwiper.tsx";
import { Spinner } from "../components/Spinner.tsx";
import { AdminPage } from "./AdminPage.tsx";
import { StatsPageContent } from "./Stats.tsx";
import { PromotePageContent } from "./Promote.tsx";
import { SettingsPageContent } from "./Settings.tsx";
import { SubscriptionsPageContent } from "./Subscriptions.tsx";
import { FriendsPageContent } from "./FriendsPage.tsx";

const TabContent = ({ tab }: { tab: Tab }) => {
  if (tab === "events") return <EventList />;
  if (tab === "taste") return <EventSwiper />;
  if (tab === "subscriptions") return <SubscriptionsPageContent />;
  if (tab === "friends") return <FriendsPageContent />;
  if (tab === "settings") return <SettingsPageContent />;
  if (tab === "stats") return <StatsPageContent />;
  if (tab === "promote") return <PromotePageContent />;
  if (tab === "admin") return <AdminPage />;
  return null;
};

export const AppPage = () => {
  const { isLoading, user, error } = db.useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = user?.email && adminEmails.includes(user.email);

  const getActiveTabFromPath = (pathname: string): Tab => {
    if (pathname.startsWith("/swipe")) return "taste";
    if (pathname.startsWith("/subscriptions")) return "subscriptions";
    if (pathname.startsWith("/friends")) return "friends";
    if (pathname.startsWith("/settings")) return "settings";
    if (pathname.startsWith("/stats")) return "stats";
    if (pathname.startsWith("/promote")) return "promote";
    if (pathname.startsWith("/admin") && isAdmin) return "admin";
    return "events";
  };

  const activeTab = getActiveTabFromPath(location.pathname);

  const handleTabChange = (tabId: Tab) => {
    if (tabId === "taste") {
      navigate("/swipe");
    } else if (tabId === "subscriptions") {
      navigate("/subscriptions");
    } else if (tabId === "friends") {
      navigate("/friends");
    } else if (tabId === "settings") {
      navigate("/settings");
    } else if (tabId === "stats") {
      navigate("/stats");
    } else if (tabId === "promote") {
      navigate("/promote");
    } else if (tabId === "admin") {
      navigate("/admin");
    } else {
      navigate("/events");
    }
  };

  const { isLoading: isUserDbLoading, data: userData } = db.useQuery(
    user?.id
      ? {
        $users: {
          $: { where: { id: user.id } },
        },
      }
      : null,
  );

  const currentUser = userData?.$users?.[0];
  const isPhoneVerified = currentUser?.phoneVerified ||
    (currentUser?.phone && currentUser.phone.length >= 7);

  if (isLoading || (user && !isAdmin && isUserDbLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black">
        <p className="text-red-400">Auth error: {error.message}</p>
      </div>
    );
  }

  if (
    !user &&
    activeTab !== "stats" &&
    activeTab !== "promote" &&
    activeTab !== "settings"
  ) {
    return <Auth />;
  }

  if (
    user && !isAdmin && !isPhoneVerified && activeTab !== "stats" &&
    activeTab !== "promote" && activeTab !== "settings"
  ) {
    return <PhoneVerification onVerified={() => {}} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black text-white md:flex">
      <AppHeader activeTab={activeTab} onTabChange={handleTabChange} />
      <main className="flex-1 min-w-0">
        <TabContent tab={activeTab} />
      </main>
    </div>
  );
};
